import { Component, Inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import type { AssignableRep } from '../board/leads-board.component';
import {
  ROLLOFF_CONTAINER_TYPES_URL, ROLLOFF_OPPORTUNITIES_URL, RollOffContainerType,
  RollOffOpportunity, SaveRollOffOpportunity,
} from './rolloff-opportunity.model';

export interface RollOffHireDialogData {
  /** The hire being edited, or null to create one. */
  hire: RollOffOpportunity | null;
  reps: AssignableRep[];
}

/**
 * The hire form — one form for create and for edit.
 *
 * One form because the server takes one request for both:
 * `SaveRollOffOpportunityRequest` is a single record, deliberately, because the
 * write is not a patch. Two forms here would be the same field list written
 * twice, and the day they drift is the day an edit silently stops clearing
 * something.
 *
 * ── THE STAGE IS NOT ON THIS FORM ─────────────────────────────────────────
 *
 * A hire starts at the front of the vocabulary — whichever stage that is; the
 * repository asks the catalog rather than naming `Quote` — and moves only by a
 * transition that records who moved it and why. A stage field here would be a
 * way to skip steps that nobody could see had been skipped.
 *
 * ── WHY `customerId` IS SENT BACK UNTOUCHED ───────────────────────────────
 *
 * Because the write is not a patch: an omitted field is not "leave it alone",
 * it is "clear it". `customerId` is the link to the account a won hire
 * created, this form has no business editing it, and leaving it out of the
 * payload would quietly break that link on the next unrelated edit. So it
 * rides along, unchanged, and is null on a create.
 *
 * ── THE CONTAINER SIZE IS A SELECT, NOT A TEXT BOX ────────────────────────
 *
 * It was a text box until 2026-09-03, with the placeholder "e.g. 30 yd", and
 * the placeholder was the problem: reps type what they mean, and "30 yd",
 * "30yd", "30 Yard" and "30-yard" are one size to a person and four to a
 * database. Now the options come from the tenant's own catalog
 * (`rolloff_container_type`), so what is stored is a row id and the wording is
 * a property of that row rather than of the keystroke.
 *
 * The options are asked for with `includeInactive=true` because this is an
 * edit form too. A tenant that stops running 40-yard boxes deactivates the row
 * so the hires already filed against it keep reading; a dropdown built only
 * from active rows would open on one of those hires with its value MISSING,
 * and the first save would silently move it to whatever the user picked
 * instead.
 */
@Component({
  selector: 'app-rolloff-hire-dialog',
  templateUrl: './rolloff-hire-dialog.component.html',
  styleUrls: ['./rolloff-hire-dialog.component.css'],
})
export class RollOffHireDialogComponent implements OnInit {
  form: FormGroup;
  saving = false;
  error = '';

  /** The sizes to offer. Empty until the catalog lands. */
  containerTypes: RollOffContainerType[] = [];

  /** Set when the catalog cannot be read, so the field says so rather than
   *  presenting an empty dropdown that looks like "no sizes exist". */
  containerTypesError = '';

  constructor(
    fb: FormBuilder,
    private http: HttpClient,
    private dialogRef: MatDialogRef<RollOffHireDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RollOffHireDialogData,
  ) {
    const hire = data.hire;
    // The lengths are the server's own, from SaveRollOffOpportunityRequest.
    // Stated here so a too-long site name is caught in the field rather than
    // as a 400 with no field attached to it.
    this.form = fb.group({
      siteName: [hire?.siteName ?? '', [Validators.required, Validators.maxLength(200)]],
      addressLine1: [hire?.addressLine1 ?? '', Validators.maxLength(200)],
      city: [hire?.city ?? '', Validators.maxLength(100)],
      postalCode: [hire?.postalCode ?? '', Validators.maxLength(20)],
      containerTypeId: [hire?.containerTypeId ?? null],
      quotedPrice: [
        hire?.quotedPrice ?? null,
        [Validators.min(0), Validators.max(99_999_999.99)],
      ],
      hireStartsOn: [hire?.hireStartsOn ?? null],
      hireEndsOn: [hire?.hireEndsOn ?? null],
      salesRepUserId: [hire?.salesRepUserId ?? null],
    });
  }

  ngOnInit(): void {
    // `includeInactive` for the reason given on this class: an edit form has
    // to be able to show a size the tenant has since retired.
    this.http.get<RollOffContainerType[]>(
      ROLLOFF_CONTAINER_TYPES_URL, { params: { includeInactive: 'true' } })
      .subscribe({
        next: sizes => { this.containerTypes = sizes ?? []; },
        // The status separates "your role lacks a permission" from "the server
        // broke", the same way the board's catalog load does.
        error: (err: { status?: number }) => {
          this.containerTypesError =
            `Container sizes could not be loaded (HTTP ${err.status ?? '?'}).`;
        },
      });
  }

  get editing(): boolean { return this.data.hire != null; }

  /**
   * The free text this hire carried before the catalog existed, when the
   * backfill could not place it — and only then.
   *
   * Shown as a note beside the dropdown rather than hidden, because it is the
   * only remaining record of what somebody wrote and the person editing this
   * hire is the one who can say which size it meant. Suppressed once a type is
   * chosen: at that point the field has an answer and the old phrase is
   * history, still on the record and no longer a question.
   */
  get unplacedSizeText(): string | null {
    const hire = this.data.hire;
    if (!hire || hire.containerTypeId != null) return null;
    const text = hire.containerSizeText?.trim();
    return text ? text : null;
  }

  /** A retired size still reads as one, so an edit form does not present the
   *  hire's own value as if it were a live choice. */
  sizeLabel(size: RollOffContainerType): string {
    return size.isActive ? size.label : `${size.label} (retired)`;
  }

  get title(): string {
    return this.editing ? 'Edit deal' : 'New roll-off deal';
  }

  repLabel(rep: AssignableRep): string {
    return rep.displayName?.trim() || rep.userName?.trim() || `Owner ${rep.userId}`;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';

    const value = this.form.getRawValue();
    const body: SaveRollOffOpportunity = {
      // Untouched, not omitted — see the note on this class.
      customerId: this.data.hire?.customerId ?? null,
      siteName: String(value.siteName).trim(),
      addressLine1: blankToNull(value.addressLine1),
      city: blankToNull(value.city),
      postalCode: blankToNull(value.postalCode),
      containerTypeId: value.containerTypeId ?? null,
      quotedPrice: value.quotedPrice === '' || value.quotedPrice == null
        ? null
        : Number(value.quotedPrice),
      hireStartsOn: blankToNull(value.hireStartsOn),
      hireEndsOn: blankToNull(value.hireEndsOn),
      salesRepUserId: value.salesRepUserId ?? null,
    };

    const saved = this.editing
      ? this.http.put<RollOffOpportunity>(
        `${ROLLOFF_OPPORTUNITIES_URL}/${this.data.hire!.id}`, body)
      : this.http.post<RollOffOpportunity>(ROLLOFF_OPPORTUNITIES_URL, body);

    saved.subscribe({
      next: hire => {
        this.saving = false;
        this.dialogRef.close(hire);
      },
      error: err => {
        this.saving = false;
        this.error = err?.error?.error
          ?? (err?.status === 403
            ? 'You do not have permission to change roll-off deals.'
            : err?.status === 404
              ? 'That deal is no longer there.'
              : 'The deal could not be saved.');
      },
    });
  }

  cancel(): void { this.dialogRef.close(null); }
}

/** An emptied text field means "clear it", which on this API is null. */
function blankToNull(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : value;
  return text ? String(text) : null;
}
